/**
 * Pure, DOM-free helpers for the shared email composer.
 *
 * The composer keeps an HTML string in React state as its single source of
 * truth. These functions translate between that canonical HTML and the
 * tiptap WYSIWYG view:
 *
 *   canonical HTML  --preprocess-->  tiptap DOM ({{x}} become merge-token pills)
 *   tiptap DOM      --postprocess--> canonical HTML (pills become {{x}} again)
 *
 * Placeholders/variables (e.g. {{first_name}}) are preserved verbatim
 * through every mode switch and are resolved per-recipient at send time by
 * the existing resolver (`src/lib/variables/resolver.ts`).
 */

const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z0-9._-]+(?:\s*[|:][^{}]+?)?)\s*\}\}/g;

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Wrap every {{placeholder}} in a merge-token span so tiptap treats it as
 * an atomic, selectable token instead of editable text.
 */
export function preprocessHtmlForTiptap(html: string): string {
  if (!html) return '';
  return html.replace(PLACEHOLDER_RE, (match, inner) => {
    const name = String(inner).trim();
    return `<span data-merge-token="${escapeAttr(name)}">${match}</span>`;
  });
}

/**
 * Convert merge-token spans back to their literal {{placeholder}} text.
 * Handles both the preprocessed spans and the spans rendered by the
 * MergeToken tiptap node (data-merge-token / data-variable).
 */
export function postprocessHtmlFromTiptap(html: string): string {
  if (!html) return '';
  return html.replace(
    /<span[^>]*data-merge-token="[^"]*"[^>]*>([\s\S]*?)<\/span>/gi,
    (_m, inner) => String(inner),
  );
}

/** Canonical round-trip used by the mode toggle: DOM → canonical HTML. */
export function canonicalizeEditorHtml(editorHtml: string): string {
  return postprocessHtmlFromTiptap(editorHtml);
}

/** List every {{placeholder}} token present in the HTML (deduped, in order). */
export function extractPlaceholders(html: string): string[] {
  if (!html) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  // Reset lastIndex: PLACEHOLDER_RE is module-global.
  PLACEHOLDER_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = PLACEHOLDER_RE.exec(html)) !== null) {
    const token = m[0];
    if (!seen.has(token)) {
      seen.add(token);
      out.push(token);
    }
  }
  PLACEHOLDER_RE.lastIndex = 0;
  return out;
}

/** Strip all tags → plain text (for text previews). */
export function stripHtmlToText(html: string): string {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Splice `insert` into `html` at a character offset (used by HTML/code
 * mode). Returns the new HTML and the cursor offset after the insert.
 */
export function insertHtmlAtOffset(
  html: string,
  insert: string,
  offset: number,
): { html: string; cursor: number } {
  const safe = Math.max(0, Math.min(offset, html.length));
  return {
    html: html.slice(0, safe) + insert + html.slice(safe),
    cursor: safe + insert.length,
  };
}

/**
 * Splice `payload` into `htmlWithMarker` at the unique cursor `marker`
 * (visual/WYSIWYG-mode insertion). The marker is kept at the end of the
 * payload so the caller can restore the caret right after it.
 * Falls back to appending when the marker is absent.
 */
export function insertPayloadAtMarker(
  htmlWithMarker: string,
  marker: string,
  payload: string,
): { html: string; markerFound: boolean } {
  if (htmlWithMarker.includes(marker)) {
    return {
      html: htmlWithMarker.replace(marker, `${payload}${marker}`),
      markerFound: true,
    };
  }
  return { html: htmlWithMarker + payload, markerFound: false };
}

/** Clamp a font size to the supported 1–50px range. */
export function clampFontSizePx(px: number): number {
  if (!Number.isFinite(px)) return 16;
  return Math.min(50, Math.max(1, Math.round(px)));
}

/** Build an email-client-safe font-size span for code-mode insertion. */
export function fontSizeSpan(px: number, inner: string): string {
  return `<span style="font-size: ${clampFontSizePx(px)}px">${inner}</span>`;
}

/** Build an email-client-safe colour span for code-mode insertion. */
export function colorSpan(color: string, inner: string): string {
  return `<span style="color: ${color}">${inner}</span>`;
}

/**
 * Wrap a code-mode selection in a block tag (paragraph / heading).
 * Used by the toolbar's block-style control in HTML mode so it behaves
 * like the visual mode's heading buttons. Empty selections produce an
 * empty block with the cursor placed inside (cursorOffset).
 */
export function wrapBlockTag(
  selected: string,
  level: 0 | 1 | 2 | 3,
): { text: string; cursorOffset?: number } {
  const tag = level === 0 ? 'p' : `h${level}`;
  if (!selected) {
    return { text: `<${tag}></${tag}>`, cursorOffset: `<${tag}>`.length };
  }
  return { text: `<${tag}>${selected}</${tag}>` };
}

/* ── Display pretty-printing for HTML/code mode (PR-11 §D) ──────
 * Inserts line breaks + indentation ONLY at block-level tag boundaries
 * and drops whitespace-only text between block tags. Text content,
 * inline markup and <pre> blocks pass through byte-identical, so the
 * formatted string renders exactly like the input and canonicalizes
 * back to the compact form on the next visual round-trip. */
const FORMAT_BLOCK_TAGS = new Set([
  'p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
  'caption', 'blockquote', 'section', 'article', 'header', 'footer', 'hr',
]);
const FORMAT_VOID_TAGS = new Set(['br', 'hr', 'img', 'input', 'meta', 'link']);

function formatTagInfo(tag: string): { name: string; closing: boolean; selfClosing: boolean } {
  const m = tag.match(/^<\s*(\/?)\s*([a-zA-Z][a-zA-Z0-9-]*)/);
  if (!m) return { name: '', closing: false, selfClosing: false };
  return {
    name: m[2].toLowerCase(),
    closing: m[1] === '/',
    selfClosing: /\/\s*>$/.test(tag),
  };
}

export function formatHtmlForDisplay(html: string): string {
  if (!html) return '';
  // Protect <pre>…</pre> verbatim (its whitespace IS content).
  const preBlocks: string[] = [];
  const protectedHtml = html.replace(/<pre[\s\S]*?<\/pre\s*>/gi, (block) => {
    preBlocks.push(block);
    return `\u0001PRE${preBlocks.length - 1}\u0002`;
  });
  const tokens = protectedHtml.match(/<[^>]+>|[^<]+/g) || [];
  let out = '';
  let depth = 0;
  let prev: 'start' | 'block' | 'inline' | 'text' = 'start';
  for (const tok of tokens) {
    if (tok.startsWith('<')) {
      const info = formatTagInfo(tok);
      if (FORMAT_BLOCK_TAGS.has(info.name)) {
        if (info.closing) depth = Math.max(0, depth - 1);
        if (prev === 'block') out += `\n${'  '.repeat(depth)}`;
        out += tok;
        if (!info.closing && !info.selfClosing && !FORMAT_VOID_TAGS.has(info.name)) depth += 1;
        prev = 'block';
      } else {
        out += tok;
        prev = 'inline';
      }
    } else if (/^\s+$/.test(tok)) {
      // Whitespace-only text: meaningful only inside inline flow.
      if (prev === 'inline' || prev === 'text') out += tok;
    } else {
      out += tok;
      prev = 'text';
    }
  }
  return out.replace(/\u0001PRE(\d+)\u0002/g, (_m, i) => preBlocks[Number(i)] || '');
}

/** Escape plain text for safe interpolation into HTML. */
export function escapeHtmlText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Convert a plain-text email body (e.g. an AI stage draft, a legacy
 * broadcast message) into simple email HTML: blank lines split
 * paragraphs, single newlines become <br/>. Merge placeholders such as
 * {{first_name}} pass through untouched (they contain no HTML chars).
 */
export function plainTextToEmailHtml(text: string): string {
  if (!text) return '';
  const normalized = text.replace(/\r\n?/g, '\n').trim();
  if (!normalized) return '';
  return normalized
    .split(/\n{2,}/)
    .map((para) => `<p>${para.split('\n').map((line) => escapeHtmlText(line)).join('<br/>')}</p>`)
    .join('');
}

/** Heuristic: does this string already contain HTML markup? */
export function looksLikeHtml(value: string): boolean {
  return /<[a-z][a-z0-9-]*(\s[^>]*)?>/i.test(value || '');
}

/**
 * Normalise an externally-supplied body (saved draft, reused campaign,
 * legacy plain-text content) into composer-ready HTML: HTML passes
 * through, plain text is paragraph-wrapped.
 */
export function toComposerHtml(value: string): string {
  if (!value) return '';
  return looksLikeHtml(value) ? value : plainTextToEmailHtml(value);
}
